import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import SentantiaLogo from '../SentantiaLogo';
import {
  ChevronDown,
  User,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { createPortal } from 'react-dom';

interface DropdownItem {
  label: string;
  path: string;
}

interface DropdownCategory {
  name: string;
  items: DropdownItem[];
}

const AI_TOOL_CATEGORIES: DropdownCategory[] = [
  {
    name: 'Jury Selection',
    items: [
      { label: 'Jury Pool', path: '/panel-pool' },
      { label: 'Jury Selection', path: '/panel-selection' },
      { label: 'Jury Questions', path: '/panel-questions' },
      { label: 'Jury Instructions', path: '/panel-instructions' },
    ],
  },
  {
    name: 'Discovery',
    items: [
      { label: 'Discovery Tracker', path: '/discovery' },
      { label: 'Depositions', path: '/depositions' },
      { label: 'Witnesses', path: '/witnesses' },
      { label: 'Experts', path: '/experts' },
      { label: 'Evidence Analyzer', path: '/evidence/analyzer' },
      { label: 'Exhibits', path: '/exhibits' },
      { label: 'Document Templates', path: '/documents' },
    ],
  },
  {
    name: 'Arguments',
    items: [
      { label: 'Generator', path: '/generator' },
      { label: 'Scenario Builder', path: '/scenarios' },
      { label: 'Templates', path: '/templates' },
      { label: 'Versions', path: '/versions' },
      { label: 'Repository', path: '/arguments' },
      { label: 'Opening', path: '/opening' },
      { label: 'Closing', path: '/closing' },
      { label: 'Cross-Exam', path: '/cross-exam' },
      { label: 'Strategy', path: '/strategy' },
      { label: 'Weakness', path: '/weakness' },
    ],
  },
  {
    name: 'Resolution',
    items: [
      { label: 'Settlement', path: '/settlement' },
      { label: 'Settlement Demand', path: '/settlement-demand' },
      { label: 'Mediation', path: '/mediation' },
      { label: 'Plea', path: '/plea' },
    ],
  },
  {
    name: 'Analysis',
    items: [
      { label: 'Compare', path: '/compare' },
      { label: 'Presentation', path: '/presentation' },
      { label: 'Case Timeline', path: '/timeline' },
      { label: 'Trial Checklist', path: '/checklist' },
    ],
  },
];

const AI_TOOL_PATHS = AI_TOOL_CATEGORIES.flatMap((c) => c.items.map((i) => i.path));

export default function TopNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [userOpen, setUserOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const aiButtonRef = useRef<HTMLButtonElement>(null);
  const aiDropdownRef = useRef<HTMLDivElement>(null);

  // Below `md` the horizontal nav (className="hidden md:flex" below) has
  // nowhere to render, so this hamburger + slide-down panel is the only way
  // to reach any nav link at narrow widths — without it, resizing the
  // window below `md` made the entire nav disappear with no replacement.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const userInitials = user?.full_name
    ? user.full_name.split(' ').map((n: string) => n[0]).join('').toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U';

  // Click-outside for AI dropdown
  useEffect(() => {
    if (!aiOpen) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const clickedButton = aiButtonRef.current?.contains(target);
      const clickedDropdown = aiDropdownRef.current?.contains(target);
      if (!clickedButton && !clickedDropdown) {
        setAiOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [aiOpen]);

  // Click-outside for user dropdown
  useEffect(() => {
    if (!userOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userOpen]);

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const aiIsActive = AI_TOOL_PATHS.some((p) => isActive(p));

  const handleAiToggle = () => {
    setAiOpen((prev) => !prev);
    setUserOpen(false);
  };

  const navLinkClass = (active: boolean) =>
    `relative flex items-center gap-1 px-4 h-16 text-sm font-medium transition-colors whitespace-nowrap ` +
    `after:absolute after:bottom-0 after:left-4 after:right-4 after:h-[3px] after:bg-forest-400 ` +
    `after:transition-opacity after:duration-200 ${
      active
        ? 'text-white after:opacity-100'
        : 'text-paper-300 hover:text-white after:opacity-0 hover:after:opacity-100'
    }`;


  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-navy-900 z-40 flex items-center justify-between px-6 lg:px-8">
      {/* Logo */}
      <Link to="/dashboard" className="shrink-0 flex items-center">
        <SentantiaLogo link={false} size="sm" className="navy" showSubtitle={false} />
      </Link>

      {/* Navigation */}
      <nav className="hidden md:flex items-stretch h-16 flex-1 justify-center gap-0">
        <Link to="/dashboard" className={navLinkClass(isActive('/dashboard'))}>
          Dashboard
        </Link>

        <Link to="/cases" className={navLinkClass(isActive('/cases'))}>
          Cases
        </Link>

        <Link to="/mock-panel" className={navLinkClass(isActive('/mock-panel'))}>
          Create Jury
        </Link>

        <Link to="/simulations/new" className={navLinkClass(isActive('/simulations/new'))}>
          Trial Simulation
        </Link>

        {/* AI Tools Dropdown Toggle */}
        <button
          ref={aiButtonRef}
          onClick={handleAiToggle}
          className={navLinkClass(aiIsActive || aiOpen)}
        >
          AI Tools
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${aiOpen ? 'rotate-180' : ''}`} />
        </button>

        <Link to="/research" className={navLinkClass(isActive('/research'))}>
          Research
        </Link>

        <Link to="/calendar" className={navLinkClass(isActive('/calendar'))}>
          Calendar
        </Link>
      </nav>

      {/* Mobile menu toggle — only path to nav links below the `md` breakpoint */}
      <button
        onClick={() => setMobileOpen((prev) => !prev)}
        className="md:hidden flex items-center justify-center w-9 h-9 text-paper-300 hover:text-white transition-colors"
        aria-label="Toggle navigation menu"
        aria-expanded={mobileOpen}
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile nav panel */}
      {mobileOpen && createPortal(
        <div className="fixed inset-x-0 top-16 bottom-0 bg-navy-900 z-50 overflow-y-auto md:hidden">
          {/* This panel is bottom-anchored and scrollable; without bottom
              safe-area padding, the last link (Calendar / last AI Tools
              item) sits under the iOS home-indicator gesture zone and taps
              near the edge are unreliable. */}
          <nav className="flex flex-col px-6 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="py-3 text-sm font-medium text-paper-300 hover:text-white border-b border-navy-800">
              Dashboard
            </Link>
            <Link to="/cases" onClick={() => setMobileOpen(false)} className="py-3 text-sm font-medium text-paper-300 hover:text-white border-b border-navy-800">
              Cases
            </Link>
            <Link to="/mock-panel" onClick={() => setMobileOpen(false)} className="py-3 text-sm font-medium text-paper-300 hover:text-white border-b border-navy-800">
              Create Jury
            </Link>
            <Link to="/simulations/new" onClick={() => setMobileOpen(false)} className="py-3 text-sm font-medium text-paper-300 hover:text-white border-b border-navy-800">
              Trial Simulation
            </Link>
            <Link to="/research" onClick={() => setMobileOpen(false)} className="py-3 text-sm font-medium text-paper-300 hover:text-white border-b border-navy-800">
              Research
            </Link>
            <Link to="/calendar" onClick={() => setMobileOpen(false)} className="py-3 text-sm font-medium text-paper-300 hover:text-white border-b border-navy-800">
              Calendar
            </Link>

            <div className="pt-4">
              <p className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider mb-2">AI Tools</p>
              {AI_TOOL_CATEGORIES.map((category) => (
                <div key={category.name} className="mb-4">
                  <p className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider mb-1">{category.name}</p>
                  <div className="flex flex-col">
                    {category.items.map((tool) => (
                      <Link
                        key={tool.path}
                        to={tool.path}
                        onClick={() => setMobileOpen(false)}
                        className="py-2 text-sm text-paper-300 hover:text-white"
                      >
                        {tool.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </nav>
        </div>,
        document.body
      )}

      {/* AI Tools Dropdown — rendered via portal outside the nav */}
      {aiOpen && createPortal(
        <div
          ref={aiDropdownRef}
          className="fixed left-0 right-0 top-16 bg-white border-b border-paper-300 shadow-xl px-6 lg:px-8 py-6 z-50"
        >
          <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-8">
            {AI_TOOL_CATEGORIES.map((category) => (
              <div key={category.name}>
                <div className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider mb-3">
                  {category.name}
                </div>
                <div className="space-y-2">
                  {category.items.map((tool) => (
                    <Link
                      key={tool.path}
                      to={tool.path}
                      onClick={() => setAiOpen(false)}
                      className={`block text-sm transition-colors ${
                        isActive(tool.path) ? 'text-forest-600 font-medium' : 'text-navy-800 hover:text-forest-600'
                      }`}
                    >
                      {tool.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* User Section */}
      <div className="flex items-center gap-3 shrink-0">
        <div className="relative" ref={userRef}>
          <button
            onClick={() => {
              setUserOpen((prev) => !prev);
              setAiOpen(false);
            }}
            className="flex items-center gap-2 text-paper-300 hover:text-white transition-colors"
            aria-label="User menu"
            aria-haspopup="true"
            aria-expanded={userOpen}
          >
            <div className="w-8 h-8 rounded-full bg-forest-500 flex items-center justify-center text-white font-semibold text-sm">
              {userInitials}
            </div>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${userOpen ? 'rotate-180' : ''}`} />
          </button>
          {userOpen && (
            <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-paper-300 rounded shadow-xl py-1 z-50">
              <button
                onClick={() => { navigate('/profile'); setUserOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-navy-800 hover:bg-paper-100 transition-colors text-left"
              >
                <User className="w-4 h-4" />
                Profile
              </button>
              <button
                onClick={() => { navigate('/settings'); setUserOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-navy-800 hover:bg-paper-100 transition-colors text-left"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
              <button
                onClick={() => { navigate('/help'); setUserOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-navy-800 hover:bg-paper-100 transition-colors text-left"
              >
                <HelpCircle className="w-4 h-4" />
                Help Center
              </button>
              <div className="border-t border-paper-200 my-1" />
              <button
                onClick={() => { logout(); setUserOpen(false); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
